import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";
import { formatBillingPeriodForGTM, sendEvent } from "../../../gtm/functions";
import { IGTMEventType } from "../../../gtm/IGTMEventType";

const Subscription = ({ subscriptionPackage }: { subscriptionPackage: Package }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const howEntriesWorkRef = useRef<HTMLDivElement>(null);
  const priceDetails = subscriptionPackage.prices.find(price => price.interval === "month");
  const [isOpenTooltip, setIsOpenTooltip] = useState(false);
  const [isOpenCollapsedContainer, setIsOpenCollapsedContainer] = useState(false);
  const subscriptionMetaData: Record<string, string> = Object.fromEntries(subscriptionPackage.metadata);
  const numberFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const features = useMemo(() => {
    const ticketPrice = 0.95;
    const totalPriceHC = Number(subscriptionMetaData["HC"]) * ticketPrice;
    const totalEntriesWithoutDreamHome =
      Number(subscriptionMetaData["total_entries"]) - Number(subscriptionMetaData["HC"]);
    return [
      {
        name: (
          <>
            <strong className="font-semibold">{subscriptionMetaData["HC"]} entries</strong> to win a Dream Home
          </>
        ),
        value: numberFormatter.format(totalPriceHC),
        hasTooltip: false,
      },
      {
        name: (
          <>
            <strong className="font-semibold">{totalEntriesWithoutDreamHome} entries</strong> in 20 competitions
          </>
        ),
        value: numberFormatter.format(Number(priceDetails.metadata.base_price.slice(1)) - totalPriceHC),
        hasTooltip: true,
      },
    ];
  }, [subscriptionPackage]);
  const collapsedBulletPoints = useMemo(
    () =>
      subscriptionPackage.features
        .filter(feature => !feature.includes("Dream Home"))
        .map(feature => {
          const entries = /(\d+) (tickets|entries),?/.exec(feature);
          return `<strong class="font-semibold">${entries?.[1]} entries</strong>${feature.replace(entries?.[0], "")}`;
        }),
    [subscriptionPackage]
  );

  useEffect(() => {
    if (isOpenTooltip) {
      document.addEventListener("click", documentClickEventHandler);
      document.addEventListener("mouseup", documentClickEventHandler);
      document.addEventListener("touchup", documentClickEventHandler);
    }

    return () => {
      document.removeEventListener("click", documentClickEventHandler);
      document.removeEventListener("mouseup", documentClickEventHandler);
      document.removeEventListener("touchup", documentClickEventHandler);
    };
  }, [isOpenTooltip]);

  const documentClickEventHandler = (e: Event) => {
    const target = e.target as HTMLElement;
    e.preventDefault();
    if (!tooltipRef.current?.contains(target) && isOpenTooltip && howEntriesWorkRef.current !== target) {
      setIsOpenTooltip(false);
    }
  };

  const planType = function (type: string) {
    if (type === "Essential") {
      return "border-black/8 bg-white";
    } else if (type === "Premium") {
      return "border-[#3E5FFF] bg-[#3E5FFF]";
    } else if (type === "Ultimate") {
      return "border-[#74B65F] bg-[#74B65F]";
    }
  };

  const index = 0;
  return (
    <>
      <div
        className={`rounded-2xl size-full lg:h-auto shadow-[0px_4px_32px_0px_rgba(0,0,0,0.10)] border ${planType(
          subscriptionPackage.name
        )}`}>
        {/*Label*/}
        <div
          className={`text-center text-white uppercase font-medium text-[10px] h-[19px] leading-5 ${
            subscriptionPackage.name === "Essential" && "rounded-t-2xl bg-white max-lg:hidden"
          }`}>
          {subscriptionPackage.name === "Premium" && <span>Most popular</span>}
          {subscriptionPackage.name === "Ultimate" && <span>Best value</span>}
        </div>

        <div className={`rounded-2xl p-4 bg-white`}>
          {/*plan type title*/}
          <div className={"w-full flex items-center justify-between mb-1.5 font-jost"}>
            <div className={"text-black text-lg leading-6 font-medium"}>{subscriptionPackage.name} Plan</div>
            <div
              className={"rounded-lg bg-[#FBEDEC] text-center px-2 py-1 text-xs text-[#9B332A] font-medium leading-4"}>
              Save {priceDetails.metadata.discount_value_card}
            </div>
          </div>
          {/*plan cost per month*/}
          <div className={"flex items-center gap-2 font-jost mb-4"}>
            <div className={"text-2xl font-bold leading-7.5"}>£{priceDetails.unit_amount / 100}</div>
            <div className={"text-xs text-dark-1 font-medium leading-4"}>
              <span className={"line-through text-sm leading-5 text-gray-1"}>{priceDetails.metadata.base_price}</span>
              /month
            </div>
          </div>
          {/*plan entries*/}
          <div className={"mb-4 relative"}>
            <div className={"mb-3 font-jost text-green-1 text-sm font-medium leading-5"}>
              <span>{subscriptionMetaData["total_entries"]}</span> entries included:
            </div>
            <div>
              {/*example #1*/}
              {features.map(feature => (
                <div key={feature.value} className={"flex items-center mb-3"}>
                  <div
                    className={clsx("flex items-center gap-1.5", {
                      "lg:cursor-pointer": isOpenTooltip,
                    })}>
                    <svg
                      className={"size-4"}
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none">
                      <path
                        d="M11.3359 2.22529C12.3414 2.80586 13.1779 3.63898 13.7625 4.64216C14.3471 5.64534 14.6595 6.78381 14.6689 7.94485C14.6783 9.1059 14.3843 10.2493 13.816 11.2618C13.2476 12.2743 12.4248 13.1208 11.4288 13.7175C10.4328 14.3142 9.29818 14.6405 8.13734 14.664C6.97649 14.6875 5.82964 14.4074 4.81031 13.8515C3.79098 13.2955 2.93452 12.483 2.32572 11.4943C1.71691 10.5056 1.37688 9.3751 1.33927 8.21463L1.33594 7.99863L1.33927 7.78263C1.37661 6.63129 1.71164 5.50927 2.3117 4.52595C2.91176 3.54264 3.75637 2.73159 4.7632 2.17188C5.77002 1.61217 6.9047 1.32289 8.05661 1.33225C9.20851 1.34161 10.3383 1.64929 11.3359 2.22529ZM10.4739 6.19396C10.3591 6.07917 10.2064 6.01022 10.0444 6.00003C9.88237 5.98985 9.72221 6.03913 9.59394 6.13863L9.53127 6.19396L7.33594 8.38863L6.47394 7.52729L6.41127 7.47196C6.28299 7.37253 6.12285 7.32331 5.96087 7.33353C5.7989 7.34375 5.64621 7.4127 5.53145 7.52747C5.41668 7.64223 5.34773 7.79492 5.33751 7.9569C5.32729 8.11887 5.37651 8.27902 5.47594 8.40729L5.53127 8.46996L6.8646 9.80329L6.92727 9.85863C7.04419 9.94934 7.18796 9.99857 7.33594 9.99857C7.48392 9.99857 7.62769 9.94934 7.7446 9.85863L7.80727 9.80329L10.4739 7.13663L10.5293 7.07396C10.6288 6.94569 10.6781 6.78552 10.6679 6.62351C10.6577 6.46149 10.5887 6.30875 10.4739 6.19396Z"
                        fill="#3DAE44"
                      />
                    </svg>
                    <div className={"font-jost text-sm font-normal leading-4 text-dark-1"}>{feature.name}</div>
                    {feature.hasTooltip ? (
                      <div
                        className={"cursor-pointer"}
                        onClick={() => {
                          setIsOpenCollapsedContainer(prevState => !prevState);
                        }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <g clipPath="url(#clip0_38854_30667)">
                            <path
                              d="M8 5.66667H8.00778M7.22222 8H8V11.1111H8.77778M1 8C1 8.91925 1.18106 9.82951 1.53284 10.6788C1.88463 11.5281 2.40024 12.2997 3.05025 12.9497C3.70026 13.5998 4.47194 14.1154 5.32122 14.4672C6.1705 14.8189 7.08075 15 8 15C8.91925 15 9.82951 14.8189 10.6788 14.4672C11.5281 14.1154 12.2997 13.5998 12.9497 12.9497C13.5998 12.2997 14.1154 11.5281 14.4672 10.6788C14.8189 9.82951 15 8.91925 15 8C15 6.14348 14.2625 4.36301 12.9497 3.05025C11.637 1.7375 9.85652 1 8 1C6.14348 1 4.36301 1.7375 3.05025 3.05025C1.7375 4.36301 1 6.14348 1 8Z"
                              stroke="#3767EB"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_38854_30667">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    ) : null}
                  </div>
                  <div className={"ml-auto text-xs font-bold text-dark-1 leading-4"}>{feature.value}</div>
                </div>
              ))}
            </div>

            {/*tooltip*/}
            <div
              ref={tooltipRef}
              className={`px-4 pt-3 pb-2 rounded-lg w-60 absolute left-1/2 -translate-x-1/2 bg-white z-10 shadow-[0px_1.5px_8px_0px_rgba(0,0,0,0.20)] ${
                isOpenTooltip ? "bottom-8" : "-top-1/2"
              } ${isOpenTooltip ? "block" : "hidden"}`}>
              <p className={"text-[13px] font-semibold mb-1 font-jost leading-4.5"}>Automatic and manual entries</p>
              <p className={"text-xs font-normal leading-4 text-gray-5"}>{subscriptionMetaData["how_it_works"]}</p>
            </div>

            {/*hidden bullets*/}
            <div
              className={`border-y border-black/8 overflow-hidden transition-all duration-500 ease-in-out ${
                isOpenCollapsedContainer ? "max-h-52 opacity-100 mb-4" : "max-h-0 opacity-0 mb-0"
              }`}>
              <div className={"p-4"}>
                {/*#A*/}
                {collapsedBulletPoints.map(bulletPoint => (
                  <div key={bulletPoint} className={"flex items-start gap-1.5 mb-4"}>
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M3.33203 7.9974L6.66536 11.3307L13.332 4.66406"
                          stroke="#3DAE44"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div
                      className={"text-sm font-normal leading-4 text-secondary font-jost"}
                      dangerouslySetInnerHTML={{ __html: bulletPoint }}
                    />
                  </div>
                ))}
              </div>
            </div>
            {/*toggle expand*/}
            <div
              ref={howEntriesWorkRef}
              className={"underline text-xs font-medium font-jost text-[#4C4C4C] leading-4 cursor-pointer"}
              onClick={() => {
                setIsOpenTooltip(prevState => !prevState);
                setIsOpenCollapsedContainer(true);
              }}>
              How entries work?
            </div>
          </div>
          {/*plan button - TO BE REPLACED*/}
          <div className={"flex-center"}>
            <button
              onClick={() => {
                sendEvent(IGTMEventType.SubscriptionPlanSelect, {
                  plan_id: subscriptionPackage.name.toLowerCase(),
                  price:
                    priceDetails.interval === "month"
                      ? priceDetails.unit_amount / 100
                      : priceDetails.metadata["monthly_price_card"],
                  billing_period: formatBillingPeriodForGTM(priceDetails.interval),
                });
                location.href = `/subscription-checkout?utm_medium=HC_drawer&packageId=${priceDetails.id}`;
              }}
              className={`theme-btn ${subscriptionPackage.name === "Premium" ? "theme-btn-primary" : "theme-btn-primary-outline text-primary"} theme-btn-md uppercase w-full btnWithArrow`}>
              <span className="relative">
                Get {subscriptionPackage.name}{" "}
                <ChevronsRightIcon
                  variant={3}
                  className={"size-6 shrink-0 absolute left-[calc(100%+theme(spacing.2))] inset-y-0 my-auto arrowImage"}
                />
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Subscription;
